"use client";

import { Check, Minus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function FlightSpecials() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const tiers = [{
    name: "ECONOMY",
    tagline: "Essential travel for the budget-conscious explorer",
    price: "R2,450",
    featured: false,
    badge: "",
    ctaLabel: "BOOK NOW",
    ctaVariant: "outline" as const,
    ctaTo: "/HomePage"
  }, {
    name: "PREMIUM",
    tagline: "Enhanced comfort with priority boarding",
    price: "R4,800",
    featured: true,
    badge: "MOST POPULAR",
    ctaLabel: "BOOK NOW",
    ctaVariant: "outline" as const,
    ctaTo: "/HomePage"
  }, {
    name: "BUSINESS",
    tagline: "Luxury suites with dedicated lounge access",
    price: "R9,600",
    featured: true,
    badge: "",
    ctaLabel: "BOOK NOW",
    ctaVariant: "primary" as const,
    ctaTo: "/HomePage"
  }];
  const features = [{
    label: "Baggage Allowance",
    values: ["20kg", "30kg", "40kg"]
  }, {
    label: "Lounge Access",
    values: ["No", "Yes", "Yes"]
  }, {
    label: "Seat Selection",
    values: ["Standard", "Extra Legroom", "Flat Bed"]
  }, {
    label: "Priority Boarding",
    values: ["No", "Yes", "Yes"]
  }, {
    label: "In-Flight Dining",
    values: ["Standard", "Premium", "Gourmet"]
  }, {
    label: "Flexible Changes",
    values: [false, true, true]
  }, {
    label: "Chauffeur Service",
    values: [false, false, true]
  }, {
    label: "Dedicated Support",
    values: [false, false, true]
  }];
  return <section data-section-id="5021" id="our-best-flight-specials" className="bg-background text-foreground py-[200px] px-6 lg:px-12 relative overflow-hidden">
      <div ref={ref} className={`max-w-[1600px] mx-auto mb-24 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-8">
          <div className="font-mono text-xs tracking-widest" style={{
          color: "var(--primary)"
        }}>CAPE TOWN / FLIGHT SPECIALS</div>
          <div className="lg:text-right">
            <h2 className="font-default font-bold text-5xl md:text-7xl lg:text-[96px] leading-[0.95] tracking-tight uppercase">
              Our Best
              <br />
              Flight Specials
              <span style={{
              color: "var(--primary)"
            }}>.</span>
            </h2>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto overflow-hidden">
        <div className="hidden lg:grid grid-cols-[220px_repeat(3,1fr)]">
          <div className="border-b border-border" />

          {tiers.map((tier, i) => <div data-index={i} key={tier.name} className={`relative border-l border-border backdrop-blur-md transition-all duration-300 hover:shadow-3xl group ${tier.featured ? "shadow-2xl" : "shadow-lg"}`} style={{
          backgroundColor: tier.featured ? "hsla(206, 67%, 39%, 0.05)" : "hsla(0, 0%, 100%, 0.5)"
        }}>
              {tier.featured && <div className="absolute top-0 left-0 right-0 h-[2px]" style={{
            backgroundColor: "var(--primary)"
          }} />}

              <div className="p-8 min-h-[280px] flex flex-col justify-between">
                <div>
                  {tier.badge && <div className="mb-4">
                      <span className="font-mono text-[10px] tracking-widest px-2 py-1 bg-primary" style={{
                  backgroundColor: "var(--secondary)",
                  color: "var(--secondary-foreground)"
                }}>
                        {tier.badge}
                      </span>
                    </div>}
                  <h3 className="font-default font-bold text-2xl xl:text-[28px] uppercase tracking-tight leading-none mb-3">
                    {tier.name}
                  </h3>
                  
                </div>
                <div className="mt-8">
                  <div className="font-default font-bold text-5xl xl:text-[72px] leading-none tracking-tight">
                    {tier.price}
                  </div>
                  <div className="font-mono text-xs tracking-widest mt-2" style={{
                color: "var(--foreground)"
              }}>
                    /PER PERSON
                  </div>
                </div>
              </div>

              <div className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300 border" style={{
            borderColor: "var(--primary)"
          }} />
            </div>)}

          {features.map((feature, i) => <div data-index={i} key={feature.label} className="contents">
              <div className="border-t border-border py-5 px-4 font-mono text-xs tracking-widest uppercase flex items-center" style={{
            color: "var(--foreground)"
          }}>
                {feature.label}
              </div>
              {feature.values.map((value, j) => <div data-index={j} key={j} className="border-t border-l border-border py-5 px-8 backdrop-blur-md flex items-center transition-all duration-300 bg-primary" style={{
            backgroundColor: tiers[j].featured ? "hsla(206, 67%, 39%, 0.02)" : "hsla(0, 0%, 100%, 0.3)"
          }}>
                  {typeof value === "boolean" ? value ? <Check className="w-5 h-5" strokeWidth={1.5} style={{
              color: "var(--secondary)"
            }} /> : <Minus className="w-5 h-5" strokeWidth={1.5} style={{
              color: "var(--foreground)"
            }} /> : <span className="font-serif text-base" style={{
              color: tiers[j].featured ? "var(--foreground)" : "var(--foreground)"
            }}>
                      {value}
                    </span>}
                </div>)}
            </div>)}

          <div className="border-t border-border" />
          {tiers.map((tier, i) => <div data-index={i} key={`cta-${tier.name}`} className="border-t border-l border-border p-8 backdrop-blur-md" style={{
          backgroundColor: tier.featured ? "hsla(206, 67%, 39%, 0.02)" : "hsla(0, 0%, 100%, 0.3)"
        }}>
              <Link to={tier.ctaTo} className="block">
                <Button variant={tier.ctaVariant === "primary" ? "default" : "outline"} size="lg" className="inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive shadow-xs rounded-md px-6 has-[>svg]:px-4 w-full font-mono text-xs tracking-widest uppercase h-12 text-secondary-foreground hover:bg-secondary/90 border-0 bg-primary">
                  {tier.ctaLabel}
                </Button>
              </Link>
            </div>)}
        </div>

        <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-6">
          {tiers.map((tier, i) => <div data-index={i} key={tier.name} className={`relative backdrop-blur-md p-8 transition-all duration-300 ${tier.featured ? "shadow-2xl" : "shadow-lg"}`} style={{
          backgroundColor: tier.featured ? "hsla(206, 67%, 39%, 0.05)" : "hsla(0, 0%, 100%, 0.5)",
          border: "1px solid var(--border)"
        }}>
              {tier.featured && <div className="absolute top-0 left-0 right-0 h-[2px]" style={{
            backgroundColor: "var(--primary)"
          }} />}

              {tier.badge && <div className="mb-4">
                  <span className="font-mono text-[10px] tracking-widest px-2 py-1" style={{
              backgroundColor: "var(--secondary)",
              color: "var(--secondary-foreground)"
            }}>
                    {tier.badge}
                  </span>
                </div>}

              <h3 className="font-default font-bold text-2xl uppercase tracking-tight mb-3">
                {tier.name}
              </h3>
              <p className="font-serif italic text-[15px] mb-6" style={{
            color: "var(--foreground)"
          }}>
                {tier.tagline}
              </p>

              <div className="mb-8 pb-8 border-b border-border">
                <div className="font-default font-bold text-5xl leading-none tracking-tight">
                  {tier.price}
                </div>
                <div className="font-mono text-xs tracking-widest mt-2" style={{
              color: "var(--foreground)"
            }}>
                  /PER PERSON
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                {features.map((feature, j) => <li data-index={j} key={feature.label} className="flex items-start justify-between gap-4 pb-3 border-b border-border">
                    <span className="font-mono text-[11px] tracking-widest uppercase" style={{
                color: "var(--foreground)"
              }}>
                      {feature.label}
                    </span>
                    <span className="text-right">
                      {typeof feature.values[i] === "boolean" ? feature.values[i] ? <Check className="w-5 h-5" strokeWidth={1.5} style={{
                  color: "var(--secondary)"
                }} /> : <Minus className="w-5 h-5" strokeWidth={1.5} style={{
                  color: "var(--foreground)"
                }} /> : <span className="font-serif text-sm">
                          {feature.values[i] as string}
                        </span>}
                    </span>
                  </li>)}
              </ul>

              <Link to={tier.ctaTo} className="block">
                <Button variant={tier.ctaVariant === "primary" ? "default" : "outline"} size="lg" className={`w-full font-mono text-xs tracking-widest uppercase h-12 ${tier.ctaVariant === "primary" ? "bg-secondary text-secondary-foreground hover:bg-secondary/90 border-0" : "bg-transparent border border-border text-foreground hover:bg-secondary hover:text-secondary-foreground hover:border-secondary"}`}>
                  {tier.ctaLabel}
                </Button>
              </Link>
            </div>)}
        </div>
      </div>
    </section>;
}